import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { resolveSupportedLanguage, supportedLanguages } from '@/i18n/config';
import { cn } from '@/lib/utils';
import { useDirection } from '@/hooks/useDirection';

interface LanguageSwitcherProps {
  variant?: 'sidebar' | 'header';
  className?: string;
}

export function LanguageSwitcher({ variant = 'header', className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const { isRtl } = useDirection();
  const activeLanguageCode = resolveSupportedLanguage(i18n.resolvedLanguage ?? i18n.language).code;
  const current = supportedLanguages.find((l) => l.code === activeLanguageCode) ?? supportedLanguages[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'gap-3 text-sm font-medium rounded-xl',
            variant === 'sidebar'
              ? 'text-muted-foreground hover:text-foreground hover:bg-muted/60 w-full justify-center px-4'
              : 'text-muted-foreground hover:text-foreground',
            className
          )}
        >
          <Globe className="w-[18px] h-[18px] flex-shrink-0" />
          <span>{current.label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === 'sidebar' ? 'center' : isRtl ? 'start' : 'end'}
        sideOffset={6}
        className={cn('min-w-[140px]', variant === 'sidebar' && 'flex w-36 flex-col items-center p-1')}
      >
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => i18n.changeLanguage(lang.code)}
            className={cn(
              'cursor-pointer gap-2',
              variant === 'sidebar' && 'mx-auto w-28 translate-x-2 justify-center px-0 text-center',
              activeLanguageCode === lang.code && 'bg-accent/10 font-medium'
            )}
          >
            <span className={cn(variant === 'sidebar' && 'block w-full text-center')}>{lang.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
